import { Button, Card, Label, Spinner, Tooltip } from "flowbite-react";
import { HiChartSquareBar } from "react-icons/hi";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useAds from "../hooks/useAds";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { staticAdsSchema } from "../schemas/static.ad.schema";
import useStaticAds from "../hooks/useStaticAds";
import { toast } from "react-toastify";
import { routes } from "../routes";
import Utils from "../utils/utils";
import { ModalAnalytics, MonSelect } from "../components";
import { useReducerAsState } from "../hooks/useReducerAsState";
import { AdAnalyticsType } from "../types/ad.analytics";

export interface AnalyticsGroupType {
  id: number;
  slug: string;
  title: string;
}

const ANALYTICS_GROUPS: AnalyticsGroupType[] = [
  {
    id: 1,
    slug: "first",
    title: "Primer grupo de publicidad",
  },
  {
    id: 2,
    slug: "second",
    title: "Segundo grupo de publicidad",
  },
  {
    id: 3,
    slug: "third",
    title: "Tercer grupo de publicidad",
  },
  {
    id: 4,
    slug: "fourth",
    title: "Cuarto grupo de publicidad",
  },
  {
    id: 5,
    slug: "fifth",
    title: "Quinto grupo de publicidad",
  },
  {
    id: 6,
    slug: "sixth",
    title: "Sexto grupo de publicidad",
  },
  {
    id: 7,
    slug: "seventh",
    title: "Séptimo grupo de publicidad",
  },
  {
    id: 8,
    slug: "eighth",
    title: "Octavo grupo de publicidad",
  },
  {
    id: 9,
    slug: "ninth",
    title: "Noveno grupo de publicidad",
  },
];

interface State {
  showModalAnalytics: boolean;
  ad: AnalyticsGroupType | null;
}

const AdsSectionsDetailsScreen = () => {
  const { id } = useParams();
  const { staticAd, loading, saveStaticAd, findStaticAd } = useStaticAds();
  const { ads, fetchAds } = useAds();

  const [state, setState] = useReducerAsState<State>({
    showModalAnalytics: false,
    ad: null,
  });

  const {
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(staticAdsSchema),
  });

  const navigate = useNavigate();

  const _setModalAnalytics = (show: boolean) =>
    setState({ showModalAnalytics: show });

  const _setAd = (ad: AnalyticsGroupType) => setState({ ad: ad });

  const initialize = () => {
    if (!staticAd) return;

    setValue(
      "ads_1",
      Utils.isEmpty(staticAd.ads_group_1) ? [] : staticAd.ads_group_1.split(",")
    );
    setValue(
      "ads_2",
      Utils.isEmpty(staticAd.ads_group_2) ? [] : staticAd.ads_group_2.split(",")
    );
    setValue(
      "ads_3",
      Utils.isEmpty(staticAd.ads_group_3) ? [] : staticAd.ads_group_3.split(",")
    );
    setValue(
      "ads_4",
      Utils.isEmpty(staticAd.ads_group_4) ? [] : staticAd.ads_group_4.split(",")
    );
    setValue(
      "ads_5",
      Utils.isEmpty(staticAd.ads_group_5) ? [] : staticAd.ads_group_5.split(",")
    );
    setValue(
      "ads_6",
      Utils.isEmpty(staticAd.ads_group_6) ? [] : staticAd.ads_group_6.split(",")
    );
    setValue(
      "ads_7",
      Utils.isEmpty(staticAd.ads_group_7) ? [] : staticAd.ads_group_7.split(",")
    );
    setValue(
      "ads_8",
      Utils.isEmpty(staticAd.ads_group_8) ? [] : staticAd.ads_group_8.split(",")
    );
    setValue(
      "ads_9",
      Utils.isEmpty(staticAd.ads_group_9) ? [] : staticAd.ads_group_9.split(",")
    );
  };

  const save = async (data: any) => {
    if (!data) return;

    const saved = await saveStaticAd({
      id,
      ...data,
    });
    toast(
      saved
        ? "Sección guardada correctamente"
        : "Ocurrió un error al guardar la sección.",
      {
        type: saved ? "success" : "error",
      }
    );

    await findStaticAd(parseInt(id!));

    if (saved && isNaN(parseInt(id!))) {
      navigate(`${routes.DASHBOARD}/${routes.ADS_SECTIONS}`, {
        replace: true,
      });
    }
  };

  useEffect(() => {
    (async () => {
      if (!id) return;
      await fetchAds({
        page: 1,
        limit: 9999999999,
      });
      await findStaticAd(parseInt(id));
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    initialize();
    // eslint-disable-next-line
  }, [staticAd]);

  const adsOptions = ads.map((ad) => ({
    value: Utils.isEmpty(ad.id) && ad.id == null ? "" : ad.id!.toString(),
    label: Utils.isEmpty(ad.title) ? "" : ad.title,
  }));

  const _getGroupValue = (field: string) => {
    if (staticAd === null) return [];

    const groupIds =
      {
        ads_1: staticAd.ads_group_1,
        ads_2: staticAd.ads_group_2,
        ads_3: staticAd.ads_group_3,
        ads_4: staticAd.ads_group_4,
        ads_5: staticAd.ads_group_5,
        ads_6: staticAd.ads_group_6,
        ads_7: staticAd.ads_group_7,
        ads_8: staticAd.ads_group_8,
        ads_9: staticAd.ads_group_9,
      }[field]?.split(",") ?? [];

    const items: any = [];
    groupIds.forEach((groupId) => {
      const group = adsOptions.find((item) => item.value === groupId);
      if (group) items.push(group);
    });
    return items;
  };

  const _onAnalyticsPressed = async (item: AnalyticsGroupType) => {
    const values = _getGroupValue(`ads_${item.id}`);

    if (values.length === 0) {
      toast("Seleccione al menos una publicidad para analizar", {
        type: "error",
      });
      return;
    }

    _setModalAnalytics(true);
    _setAd(item);
  };

  const _render = () => {
    if (loading) return <p>Cargando...</p>;
    return (
      <form onSubmit={handleSubmit(save)}>
        <div className="m-4">
          <div className="mb-4">
            <div className="flex flex-row justify-between">
              <h1>Detalles de la sección</h1>
              <Button isProcessing={loading} disabled={loading} type="submit">
                Guardar sección
              </Button>
            </div>
          </div>
          <Card>
            {ANALYTICS_GROUPS.map((item) => (
              <div className="flex flex-col">
                <div className="mb-2 flex flex-row items-center">
                  <Label htmlFor={`ads_${item.id}`} value={item.title} />
                  <Button
                    color="gray"
                    onClick={() => _onAnalyticsPressed(item)}
                    className="ml-2"
                  >
                    <Tooltip content="Analíticas">
                      <HiChartSquareBar />
                    </Tooltip>
                  </Button>
                </div>
                <Controller
                  key={item.id}
                  name={`ads_${item.id}`}
                  control={control}
                  render={({ field }) => (
                    <MonSelect
                      id={`ads_${item.id}`}
                      {...field}
                      defaultValue={_getGroupValue(`ads_${item.id}`)}
                      options={adsOptions}
                      isMulti={true}
                      onChange={(value: any) => {
                        const values = value.map((item: any) => item.value);
                        field.onChange(values);
                      }}
                      helperText={
                        errors[`ads_${item.id}`] !== undefined
                          ? errors[`ads_${item.id}`]?.message?.toString()
                          : undefined
                      }
                      color={errors[`ads_${item.id}`] ? "failure" : "gray"}
                    />
                  )}
                />
              </div>
            ))}
          </Card>
        </div>
      </form>
    );
  };

  return (
    <>
      {_render()}
      {state.showModalAnalytics && state.ad && (
        <ModalAnalytics
          show={state.showModalAnalytics}
          groupId={parseInt(id!)}
          data={state.ad}
          onClose={() => setState({ showModalAnalytics: false, ad: null })}
        />
      )}
    </>
  );
};

export default AdsSectionsDetailsScreen;
